import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, Divider, CircularProgress, List,
  ListItemButton, ListItemAvatar, Avatar, ListItemText, IconButton,
  Badge, Drawer, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, TextField, Button, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddCommentIcon from '@mui/icons-material/AddComment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import API from '../../../API';
import ChatScreen from '../../../Components/ChatScreen';
import useIsDesktop from '../../../Hooks/useIsDesktop';
import useAuth from '../../../Hooks/use-auth';
import { useCreatorAuth } from '../../../Hooks/creator-use-auth';
import ParticipantsPanel from '../../../Components/ChatComponents/chatextra';
import ChatModalCreator from '../../../Components/ChatComponents/creatorchatmodal';
import CreateNewChatDialog from '../../../Components/ChatComponents/newchat';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
const BOTTOM_NAV_HEIGHT = 86;                              // matches AppShell
const SAFE_BOTTOM       = 'env(safe-area-inset-bottom,0)'; // iOS home-bar inset

export default function CreatorConversationsHub() {
  const isMobile = !useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /* ---------------- auth ---------------- */
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const { creatorToken } = useCreatorAuth();
  const userId =
    userInfo?.id || creatorToken?.creator_user?.username || null;

  /* ---------------- state ---------------- */
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // new‑chat picker dialog
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // modal that appears after picker (for single‑user chat)
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [newChatUserId, setNewChatUserId] = useState(null);

  /* ---------------- queries ---------------- */
  const {
    data: conversations = [],
    isLoading: loadingConvos,
    error: convError,
    refetch: refetchConversations
  } = useQuery('creatorConversations', API.conversations.fetchCreatorConversations);

  const {
    data: publicUsers = [],
    isLoading: loadingUsers
  } = useQuery('publicUsers', API.users.publicList, {
    refetchOnWindowFocus: false
  });

  /* build options for Autocomplete */
  const userOptions = useMemo(
    () =>
      publicUsers.map((u) => ({
        label: `${u.first_name} ${u.last_name} (${u.id})`,
        value: u.id
      })),
    [publicUsers]
  );

  /* ---------------- mutations ---------------- */
  const createChat = useMutation(
    (body) => API.conversations.checkCreatorToUser(body),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('creatorConversations');
        setNewChatOpen(false);
        setSelectedUsers([]);
        // jump in
        handleSelectConversation(data.conversation_id);
      }
    }
  );

  /* ---------------- URL sync ---------------- */
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const convoId = qs.get('conversation');
    if (convoId) {
      setSelectedConversationId(Number(convoId));
      refetchConversations(); // Refresh conversations when loading from URL
    }

    const withId = qs.get('with');
    if (withId) {
      setNewChatUserId(Number(withId));
      setChatModalOpen(true);
    }
  }, [location.search, refetchConversations]);

  /* ---------------- helpers ---------------- */
  const getConvoName = (convo) => {
    if (convo.display_name) return convo.display_name;
    if (convo.campaign_name) return convo.campaign_name;
    if (convo.partnership_name) return convo.partnership_name;
    const other = convo.participants?.find((p) => p.id !== userId);
    return other
      ? `${other.first_name} ${other.last_name}`
      : `Conversation #${convo.id}`;
  };

  const filteredConversations = useMemo(() => {
    if (!searchQuery) return conversations;
    const lowerQuery = searchQuery.toLowerCase();
    return conversations.filter((convo) =>
      getConvoName(convo).toLowerCase().includes(lowerQuery)
    );
  }, [conversations, searchQuery]);

  const selectedConversation = conversations.find(
    (c) => c.id === selectedConversationId
  );

  /* ---------------- ui handlers ------------- */
  const toggleLeftPanel = () => setIsLeftPanelCollapsed((p) => !p);

  const handleSelectConversation = (id) => {
    setSelectedConversationId(id);
    navigate(`?conversation=${id}`, { replace: true });
    if (isMobile) setIsLeftPanelCollapsed(true);
  };

  const handleBackToList = () => {
    setSelectedConversationId(null);
    navigate('', { replace: true });
    if (isLeftPanelCollapsed) setIsLeftPanelCollapsed(false);
    refetchConversations(); // Refresh conversations when going back
  };

  const launchNewChatPicker = () => setNewChatOpen(true);

  const handleCreateChat = () => {
    if (!selectedUsers.length) return;
    if (selectedUsers.length === 1) {
      setNewChatUserId(selectedUsers[0].value);
      setChatModalOpen(true); // will create via modal
    } else {
      createChat.mutate({
        recipient_ids: selectedUsers.map((u) => u.value)
      });
    }
  };

  /* ---------------- loading / error ---------------- */
  if (loadingConvos)
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress />
      </Box>
    );
  if (convError)
    return (
      <Box sx={{ p: 2, color: 'red' }}>
        Error: {convError.message}
      </Box>
    );

  /* ------------------------------------------------------------------ */
  /*  render                                                            */
  /* ------------------------------------------------------------------ */
 return (
   <Box
        sx={{
          display: 'flex',
          height: isMobile
            ? `calc(100dvh - ${BOTTOM_NAV_HEIGHT}px - ${SAFE_BOTTOM})`
            : '100dvh',
          width: '100%',
          overflow: 'hidden',
          bgcolor: 'background.default',
        }}
      >
      {/* Left panel */}
      <Card
        sx={{
          m: 1,
          width: isLeftPanelCollapsed ? 0 : { xs: '100%', md: 400 },
          minWidth: isLeftPanelCollapsed ? 0 : { xs: '100%', md: 400 },
          height: '100%',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          position: { xs: 'absolute', md: 'relative' },
          zIndex: 1,
          left: isLeftPanelCollapsed ? '-100%' : 0,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6">Conversations</Typography>
            {!isMobile && (
              <IconButton onClick={toggleLeftPanel}>
                <ChevronRightIcon />
              </IconButton>
            )}
          </Box>
          {/* Search bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              )
            }}
            sx={{ mb: 1 }}
          />
          {/* New chat button */}
          <Button
            variant="contained"
            startIcon={<AddCommentIcon />}
            onClick={launchNewChatPicker}
            fullWidth
          >
            Start New Chat
          </Button>
        </Box>
        <Divider />
        {/* list */}
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', pb: isMobile ? `calc(${BOTTOM_NAV_HEIGHT}px + ${SAFE_BOTTOM})` : 4 }}>
          <List>
            {filteredConversations.map((convo) => (
              <ListItemButton
                key={convo.id}
                onClick={() => handleSelectConversation(convo.id)}
                selected={convo.id === selectedConversationId}
              >
                <ListItemAvatar>
                  <Avatar
                    src={
                      convo.participants.find((p) => p.id !== userId)?.phphref
                    }
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Typography variant="subtitle1" noWrap>
                        {getConvoName(convo)}
                      </Typography>
                      <Badge
                        badgeContent={convo.unread_count}
                        color="primary"
                        max={999}
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.7rem',
                            minWidth: 18,
                            height: 18
                          }
                        }}
                      >
                        <ChatBubbleOutlineIcon fontSize="small" />
                      </Badge>
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                    >
                      {convo.message || 'No messages yet'}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Card>

      {/* Right panel */}
      <Card
        sx={{
          flex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          m            : { xs: 0, md: 1 },          // ← NEW
          borderRadius : { xs: 0, md: 2 }           // ← NEW
        }}
      >
        {/* header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMobile && (
              <IconButton onClick={toggleLeftPanel}>
                {isLeftPanelCollapsed ? <MenuIcon /> : <ChevronRightIcon />}
              </IconButton>
            )}
            {isMobile && selectedConversationId && (
              <IconButton onClick={handleBackToList}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography variant="h6" noWrap>
              {selectedConversation
                ? selectedConversation.display_name ||
                  getConvoName(selectedConversation)
                : selectedConversationId
                ? 'Conversation'
                : 'Select a Conversation'}
            </Typography>
          </Box>

          {selectedConversationId && (
            <IconButton onClick={() => setParticipantsOpen(true)}>
              <PersonAddIcon />
            </IconButton>
          )}
        </Box>
        <Divider />

        {selectedConversationId ? (
          <ChatScreen
            conversationId={selectedConversationId}
            sx={{ flex: 1, minHeight: 0 }}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Select a conversation
            </Typography>
          </Box>
        )}
      </Card>

      {/* participants drawer */}
      <Drawer
        anchor="right"
        open={participantsOpen}
        onClose={() => setParticipantsOpen(false)}
      >
        <ParticipantsPanel
          conversationId={selectedConversationId}
          onClose={() => setParticipantsOpen(false)}
        />
      </Drawer>

      {/* picker dialog */}
      <CreateNewChatDialog
           open={newChatOpen}
           onClose={() => setNewChatOpen(false)}
           onCreated={(convoId) => {
             queryClient.invalidateQueries('creatorConversations');
             handleSelectConversation(convoId);
           }}
         />

      {/* single‑user modal (creator_to_user) */}
      <ChatModalCreator
        open={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        recipientUserId={newChatUserId}
      />
    </Box>
  )
}